import { UserRole } from '@repo/auth/definitions/user-role';

import { FeatureFlag } from '~/definitions/feature-flag';
import { RouteConfig } from '~/definitions/routes';

type NavLink = {
  children?: NavLink[];
  isActive?: boolean;
  isDisabled?: boolean;
  label: string;
  onClick?: () => void;
};

type BuildNavLink = NavLink & {
  children: BuildNavLink[];
};

type BuildNavLinksFromRoutesParams = {
  featureFlags: Partial<Record<FeatureFlag, boolean>>;
  hasSession: boolean;
  onNavbarClick: (page: string) => void;
  pathname: string;
  routeEntries: [string, RouteConfig][];
  userRoles: UserRole[];
};

const isRouteVisible = (
  routeInfo: RouteConfig,
  hasSession: boolean,
  featureFlags: Partial<Record<FeatureFlag, boolean>>,
  userRoles: UserRole[],
) => {
  if (routeInfo.isPublic) return true;
  if (!hasSession) return false;

  if (routeInfo.featureFlag && !featureFlags[routeInfo.featureFlag]) {
    return false;
  }

  if (!routeInfo.requiredRoles) return true;

  return routeInfo.requiredRoles.some((role) => userRoles.includes(role));
};

const finalizeLinks = (links: BuildNavLink[]): NavLink[] =>
  links.map((link) => {
    const children = finalizeLinks(link.children);
    const isActive = Boolean(link.isActive) || children.some((child) => Boolean(child.isActive));

    return {
      ...(children.length ? { children } : {}),
      ...(isActive ? { isActive } : {}),
      ...(link.isDisabled !== undefined ? { isDisabled: link.isDisabled } : {}),
      label: link.label,
      ...(link.onClick ? { onClick: link.onClick } : {}),
    };
  });

export const buildNavLinksFromRoutes = ({
  featureFlags,
  hasSession,
  onNavbarClick,
  pathname,
  routeEntries,
  userRoles,
}: BuildNavLinksFromRoutesParams): NavLink[] => {
  const rootLinks: BuildNavLink[] = [];
  const groupLinksByPath = new Map<string, BuildNavLink>();

  routeEntries
    .filter(([_route, routeInfo]) => routeInfo.isTopNavigation && routeInfo.label)
    .filter(([_route, routeInfo]) => isRouteVisible(routeInfo, hasSession, featureFlags, userRoles))
    .forEach(([route, routeInfo]) => {
      const navigationParentLabels =
        routeInfo.navigationParentLabels ?? (routeInfo.navigationParentLabel ? [routeInfo.navigationParentLabel] : []);

      let currentLevelLinks = rootLinks;
      let parentPath = '';

      navigationParentLabels.forEach((parentLabel) => {
        parentPath = parentPath ? `${parentPath}::${parentLabel}` : parentLabel;
        const existingParentLink = groupLinksByPath.get(parentPath);

        if (existingParentLink) {
          currentLevelLinks = existingParentLink.children;
          return;
        }

        const parentLink: BuildNavLink = {
          children: [],
          label: parentLabel,
        };

        groupLinksByPath.set(parentPath, parentLink);
        currentLevelLinks.push(parentLink);
        currentLevelLinks = parentLink.children;
      });

      currentLevelLinks.push({
        children: [],
        isActive:
          pathname === route ||
          pathname.startsWith(`${route}/`) ||
          routeInfo.activeFor?.some((base) => pathname === base || pathname.startsWith(`${base}/`)),
        isDisabled: pathname === route,
        label: routeInfo.label!,
        onClick: () => onNavbarClick(`${route}${routeInfo.initialParams ? `/?${routeInfo.initialParams}` : ''}`),
      });
    });

  return finalizeLinks(rootLinks);
};
